import {Component, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ComsumePaymentData, NoticeType, RequestWithDuplicata} from '../../../shared/models/request';
import {ApiService} from '../../../shared/services/api.service';
import {DocumentType} from '../../../shared/models/user';
import {FormService, LoaderService, NavigateService, ShortcutAction} from 'ngx-satoris';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {addYears, addDays, format, isAfter, isBefore} from 'date-fns';
import {Subscription} from 'rxjs';
import {PaymentService} from '../../../shared/services/payment.service';
import {PlacesService} from '../../../shared/services/places.service';
import {QueueService} from '../../../shared/services/queue.service';
import {PrintService} from '../../../shared/services/print.service';
import {AllDocumentsPrintType, PrintNoticeData, PrintNoticeRestrictionData} from '../../../shared/models/print';
import {getNoticeFormConfig, referenceSchema} from '../../../shared/models/default-filter-strategy';
import {FormField} from '../../../shared/models/forms';

@Component({
  selector: 'app-admin-request-notice',
  templateUrl: './admin-request-notice.component.html',
  styleUrl: './admin-request-notice.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AdminRequestNoticeComponent {

  requestId: string;
  current: RequestWithDuplicata;
  form: FormGroup;
  fromRoute: string;
  submitted = false;
  currentNotice: NoticeType;
  noticeFields: FormField[] = [];
  refusalReasonEnum: string[];
  restrictionReportingPostEnum: string[];
  restrictionDocumentToProduceEnum: string[];
  reportingReportingPostEnum: string[];
  query: any;
  backParams: any;
  qrCode: string;
  validatorDates = {
    minDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    maxDate: format(addYears(new Date(), 125), 'yyyy-MM-dd')
  };

  subscription: Subscription;
  printSubscription: Subscription;
  paymentSubscription: Subscription;

  constructor(public api: ApiService,
    public nav: NavigateService,
    public forms: FormService,
    private formBuilder: FormBuilder,
    private queue: QueueService,
    private places: PlacesService,
    private print: PrintService,
    private payment: PaymentService,
    private loader: LoaderService,
    private route: ActivatedRoute) {}

  ngOnInit() {
    this.loader.loading(true);
    this.places.getPlace(this.api.userPlaceId, false, true).finally(() => {
      this.api.schema(referenceSchema + '-consume', true).then((res) => {
        this.refusalReasonEnum = res.properties.Documents.items.properties.RefusalFields.properties.Reason.enum;
        this.restrictionReportingPostEnum = res.properties.Documents.items.properties.RestrictionFields.properties.ReportingPost.enum;
        this.restrictionDocumentToProduceEnum = res.properties.Documents.items.properties.RestrictionFields.properties.DocumentToProduce.items.enum;
        this.reportingReportingPostEnum = res.properties.Documents.items.properties.ReportingFields.properties.ReportingPost.enum;

        // Keep simple min/max for date inputs: min is tomorrow, max is +100 days
        this.validatorDates = {
          minDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
          maxDate: format(addDays(new Date(), 100), 'yyyy-MM-dd')
        };

        this.subscription = this.route.paramMap.subscribe(params => {
          this.requestId = params.get('paymentId');
          if(this.requestId && this.requestId !== 'undefined') this.payment.getPayment(this.requestId, true, {history: true, watchlist: false, apipnr: false, inbound: false, fullMetadata: false}, true);

          this.route.queryParams.subscribe(params => {
            const {createdAt, paymentId, placeID, userPlaceID, operation, from, paymentReference, occupationMedia, fromScanPassport, fromNoticePrint, fromScanFingerprint, cashType, notice, referencePrint, datePrint, nameClient, amount, currency, agentId, typePayment, placeId, buyDocument, idRequest, paymentIsNotDocument, borderPass, purposeOfVisit, skipCamera, refuseFingerPrint, forceOperation, offlineApplication, supervisor, printOverstayReceipt, ...rest} = params;
            this.query = {createdAt, paymentId, placeID, userPlaceID, operation, from, paymentReference, occupationMedia, fromNoticePrint, fromScanPassport, cashType, notice, fromScanFingerprint, referencePrint, datePrint, nameClient, amount, currency, agentId, typePayment, placeId, buyDocument, idRequest, paymentIsNotDocument, borderPass, purposeOfVisit, skipCamera, refuseFingerPrint, forceOperation, offlineApplication, supervisor, printOverstayReceipt, ...rest};
            this.backParams = this.query.fromScanFingerprint === 'true' ? this.query : {from: this.query.from, ...rest};
            this.fromRoute = params.from || '';
            this.currentNotice = params.notice || null;
            if(offlineApplication) {
              this.current =  JSON.parse(offlineApplication);
              this.query.notice && this.setForm();
              this.queue.onNavDeleteOfflineQueueId = this.current.queueId;
            } else {
              this.queue.onNavDeleteOfflineQueueId = undefined;
            }
          });
        });

        if(!this.query.offlineApplication) {
          this.paymentSubscription = this.payment.current$.subscribe((payment) => {
            if(!payment || this.current && this.current.id !== payment.id) this.current = undefined;
            this.current = payment;
            if(this.current) this.loader.loading(false);
            this.query.notice && this.setForm();
          });
          this.payment.getPayment(this.requestId, true, {history: true, watchlist: false, apipnr: false, inbound: false, fullMetadata: false}, true);
        }
        this.loader.loading(false);
      }).catch(() => {
        this.loader.loading(true, {type: 'error', message: 'global.error'});
      });
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.printSubscription?.unsubscribe();
    this.paymentSubscription?.unsubscribe();
  }

  setForm() {
    this.submitted = false;
    if(!this.currentNotice || !this.current) return;
    const dateRangeValidator: ValidatorFn = (form: FormGroup): ValidationErrors | null => {
      const startControl = form.get('periodFrom');
      const endControl = form.get('periodTo');
      if(!startControl || !endControl) return null;
      const startDate = new Date(startControl.value);
      const endDate = new Date(endControl.value);
      //if startControl is before today, set error
      if(isBefore(startDate, new Date())) {
        startControl.setErrors({...startControl.errors, minDate: true});
      }
      //if endControl is before startControl, set error
      if(startControl.value && endControl.value && isAfter(startDate, endDate)) {
        startControl.setErrors({...startControl.errors, periodFromGreaterThanPeriodTo: true});
      } else {
        if(startControl.errors) {
          delete startControl.errors.periodFromGreaterThanPeriodTo;
          if(!Object.keys(startControl.errors).length) {
            startControl.setErrors(null);
          }
        }
      }
      return null;
    };

    const step = getNoticeFormConfig()[this.currentNotice];
    if(!step?.fields) return;
    this.noticeFields = step.fields.map((field) => ({...field}));
    this.applyRuntimeFieldData();

    this.form = this.formBuilder.group({});
    for(const field of this.noticeFields) {
      if(field.type === 'dateRange') {
        this.form.addControl(field.name + 'From', this.formBuilder.control('', this.staticValidators(field)));
        this.form.addControl(field.name + 'To', this.formBuilder.control('', this.staticValidators(field)));
      } else if(field.type === 'checkbox' && field.enum) {
        field.enum.forEach((option: string) => this.form.addControl(option, this.formBuilder.control('')));
      } else {
        this.form.addControl(field.name, this.formBuilder.control({value: field.value ?? '', disabled: !!field.disabled}, this.staticValidators(field)));
      }
    }
    if(this.currentNotice === NoticeType.REPORTING) {
      this.form.setValidators(dateRangeValidator);
      setTimeout(() => {
        //TODO component needs an update in the library to handle better default values and error style
        this.form.get('periodFrom')?.setValue(undefined);
        this.form.get('periodTo')?.setValue(undefined);
      }, 100);
    }
  }

  /** Inject runtime data (current payment, place, officer, backend schema enums) into the cloned config fields. */
  private applyRuntimeFieldData() {
    const meta = this.current.metadata;
    const fullNames = (meta?.FirstName || '') + (meta?.LastName ? ' ' + meta.LastName : '');
    const station = this.places.currentPlace ? this.places.currentPlace.longName + ' [' + this.places.currentPlace.id + ']' : '';
    const officer = this.api.userInfo.name + ' [' + this.api.userInfo.id + ']';
    for(const field of this.noticeFields) {
      switch(field.name) {
      case 'hqReference':
        field.value = this.current.internalReference || '';
        field.disabled = !!this.current.internalReference;
        break;
      case 'stationReference':
        field.value = station;
        field.disabled = !!this.places.currentPlace;
        break;
      case 'fullNames':
        field.value = fullNames;
        field.disabled = !!(meta?.FirstName && meta?.LastName);
        break;
      case 'officer':
        field.value = officer;
        field.disabled = this.currentNotice === NoticeType.RESTRICTION || !!this.api.userInfo.name;
        break;
      case 'reportLocation':
        if(this.currentNotice === NoticeType.RESTRICTION) {
          field.enum = this.restrictionReportingPostEnum;
        } else {
          field.value = station;
          field.disabled = !!this.places.currentPlace;
        }
        break;
      case 'reason':
        field.enum = this.refusalReasonEnum;
        break;
      case 'reportAt':
        field.enum = this.reportingReportingPostEnum;
        break;
      case 'documentToProduce':
        field.enum = this.restrictionDocumentToProduceEnum;
        break;
      case 'residentialAddress':
      case 'addressZimbabwe':
        field.value = meta?.AddressZimbabwe || '';
        break;
      case 'dateOfRefusal':
        field.value = format(new Date(), 'yyyy-MM-dd');
        field.disabled = true;
        break;
      case 'place':
        field.value = station;
        field.disabled = !!this.places.currentPlace;
        break;
      case 'reportOn':
        field.dateValidators = {...field.dateValidators, minDate: this.validatorDates.minDate, maxDate: this.validatorDates.maxDate};
        break;
      }
    }
  }

  private staticValidators(field: FormField): ValidatorFn[] {
    return (field.validators || []).filter((v): v is ValidatorFn => typeof v !== 'string');
  }

  get columnOneFields(): FormField[] {
    return this.noticeFields.filter((field) => field.customClass !== 'col2');
  }

  get columnTwoFields(): FormField[] {
    return this.noticeFields.filter((field) => field.customClass === 'col2');
  }

  isFieldVisible(field: FormField): boolean {
    if(field.hidden) return false;
    if(field.ifFormControl && !this.form.get(field.ifFormControl)?.value) return false;
    if(field.ifFormControlHasValue) {
      const value = this.form.get(field.ifFormControlHasValue.control as string)?.value;
      return typeof value === 'string' && value.includes(field.ifFormControlHasValue.value);
    }
    return true;
  }

  submit() {
    this.submitted = true;
    if(this.forms.getFormErrors(this.form).length < 1) {
      this.submitted = false;
      const docs: string[] = this.restrictionDocumentToProduceEnum?.filter((doc) => this.form.value[doc]);
      if(this.form.value.otherMissingDocument?.trim()) docs.push('Other');
      const data = {
        ProfilePicture: undefined,
        Operation: this.currentNotice === NoticeType.REFUSAL ? this.query.operation : undefined,
        OperationComments: this.form.value.operationComments || undefined,
        PaymentReference: undefined,
        PaymentMode: undefined,
        PaymentCurrency: undefined,
        Documents: [{
          Type: this.currentNotice,
          RefusalFields: this.currentNotice === NoticeType.REFUSAL ? {
            Paragraph: this.form.value.paragraph?.trim() || undefined,
            Reason: this.form.value.reason || undefined
          } : undefined,
          RestrictionFields: this.currentNotice === NoticeType.RESTRICTION ? {
            ReportingPost: this.form.value.reportLocation || undefined,
            GracePeriodDays: Number(this.form.value.daysUntilReport) || undefined,
            ResidentialAddress: this.form.value.residentialAddress?.trim() || undefined,
            BusinessAddress: this.form.value.businessAddress?.trim() || undefined,
            Phone: this.form.value.phone?.trim() || undefined,
            Email: this.form.value.email?.trim() || undefined,
            DocumentToProduce: docs || undefined,
            DocumentToProduceOther: this.form.value.otherMissingDocument?.trim() || undefined
          } : undefined,
          ReportingFields: this.currentNotice === NoticeType.REPORTING ? {
            Purpose: this.form.value.purpose || undefined,
            Host: this.form.value.host?.trim() || undefined,
            HostPhone: this.form.value.hostPhone?.trim() || undefined,
            AddressZimbabwe: this.form.value.addressZimbabwe?.trim() || undefined,
            Phone: this.form.value.phone?.trim() || undefined,
            Email: this.form.value.email?.trim() || undefined,
            Address: this.form.value.address?.trim() || undefined,
            ReportingPost: this.form.value.reportAt || undefined,
            ExpectedDeparture: new Date(this.form.value.periodTo)?.getTime() || undefined,
            ReportOn: new Date(this.form.value.reportOn)?.getTime() || undefined,
            ReportAtIntervalDays: Number(this.form.value.reportInterval) || undefined,
            PeriodFrom: new Date(this.form.value.periodFrom)?.getTime() || undefined,
            PeriodTo: new Date(this.form.value.periodTo)?.getTime() || undefined
          } : undefined
        }]
      } as ComsumePaymentData;

      Object.keys(data).forEach((key: keyof ComsumePaymentData) => {
        if(data[key] === undefined) {
          delete data[key];
        }
      });

      const noticeData: PrintNoticeData | PrintNoticeRestrictionData = {
        form: this.form,
        qrCode: this.qrCode,
        documents: this.currentNotice === NoticeType.RESTRICTION ? this.docsToProducePrint() : undefined
      };
      this.print.print(this.currentNotice, noticeData);

      this.printSubscription = this.print.printLoaded.subscribe((loaded: AllDocumentsPrintType) => {
        if(loaded) {
          this.loader.loading(true, {type: 'question', message: 'print.isDone', btnLabel: 'yes', custom: {closeBtnLabel: 'no'}}).then((done: boolean) => {
            if(done) {
              window.sessionStorage.setItem('noticePrint', JSON.stringify(data));
              this.nav.to('admin-request-face-check', undefined, {queryParams: {...this.query, fromNoticePrint: true}});
            } else {
              this.print.print(this.currentNotice, noticeData);
            }
          });
        }
      });
    }
  }

  selectNotice(notice: NoticeType) {
    this.currentNotice = notice;
    this.setForm();
  }

  docsToProducePrint(): string[] {
    return this.restrictionDocumentToProduceEnum.filter((doc) => this.form.value[doc]);
  }

  /** Toggle visibility/validators of fields conditioned on a checkbox (FormField.ifFormControl). */
  checkIfFormControl(event: any, doc: string) {
    this.noticeFields.filter((field) => field.ifFormControl === doc).forEach((dependent) => {
      const control = this.form.get(dependent.name);
      if(!control) return;
      if(event.checked) {
        if(dependent.requiredIfFormControl) control.addValidators(Validators.required);
        setTimeout(() => {
          document.getElementById(dependent.name)?.focus();
        });
      } else {
        control.setValue('');
        control.setValidators(this.staticValidators(dependent));
      }
      control.updateValueAndValidity();
    });
  }

  protected readonly DocumentType = DocumentType;
  protected readonly ConsumePaymentNoticeType = NoticeType;

  /** Toggle validators of fields conditioned on another field's value (FormField.ifFormControlHasValue). */
  onFieldOutput(field: FormField, event: any) {
    this.noticeFields.filter((dependent) => dependent.ifFormControlHasValue?.control === field.name).forEach((dependent) => {
      const control = this.form.get(dependent.name);
      if(!control) return;
      if(typeof event === 'string' && event.includes(dependent.ifFormControlHasValue?.value)) {
        const validators = this.staticValidators(dependent);
        control.setValidators(dependent.requiredIfFormControl ? [...validators, Validators.required] : validators);
      } else {
        control.setValue('');
        control.clearValidators();
      }
      control.updateValueAndValidity();
    });
  }

  goBack() {
    if(this.query.offlineApplication) {
      this.nav.goBack();
    } else if((this.query.fromScanFingerprint === 'true' && this.currentNotice === null) || this.query.refuseFingerPrint === 'true') {
      this.nav.to('admin-scan-fingerprint', undefined, {queryParams: this.backParams});
    } else if(this.fromRoute && (this.query.notice || this.currentNotice === null)) {
      this.nav.to(this.fromRoute, undefined, {queryParams: this.backParams});
    } else {
      this.currentNotice = null;
    }
  }

  protected readonly ShortcutAction = ShortcutAction;
}
